// As regras do CRM das licencas. Puras: recebem fatos, devolvem classificacao. Ficam aqui, longe da
// tela e do banco, porque e sobre elas que o dono vai tomar decisao comercial — e uma regra enterrada
// num `.tsx` nao se le nem se testa.
//
// Decidido com o dono em 19/10 (hub#62).

import { TOKEN_LIFETIME_SECONDS } from "./ping";

// —— O que e uma venda ————————————————————————————————————————————————————————

// 🔴 O hub NAO adivinha. Quem cunha a licenca diz o que ela e. Deduzir "vendida" de qualquer sinal
// automatico (tem instalacao? esta ativa?) poria as licencas de teste no faturamento.
export type Venda = "sold" | "trial" | "courtesy";

export const VENDAS: Venda[] = ["sold", "trial", "courtesy"];

export function ehVenda(v: unknown): v is Venda {
  return typeof v === "string" && (VENDAS as string[]).includes(v);
}

// —— O sinal: ha quanto tempo a licenca nao fala com o hub ————————————————————

// 🔴 O corte NAO e um numero escolhido a dedo: e o tempo de vida do token. Uma instalacao renova a
// cada ping, entao passar desse prazo sem falar significa que ela **deixou de estar licenciada de
// fato** — nao e "talvez esteja quieta", e o estado que o produto ja aplica sozinho.
//
// Amarrar ao mesmo numero tambem e o que impede a tela de discordar do produto no dia em que o
// prazo mudar.
export const SEM_SINAL_SEGUNDOS = TOKEN_LIFETIME_SECONDS;

// ⚠️ "nunca" NAO e "ha muito tempo". Uma licenca cunhada hoje, cujo cliente ainda nao instalou, nao
// e um cliente que sumiu — e uma venda que ainda nao chegou ao servidor. Juntar as duas faria o
// console mandar cobrar explicacao de quem acabou de comprar.
export type Sinal = "viva" | "sumida" | "nunca";

export function sinalDe(ultimoContato: Date | null, agora: Date): Sinal {
  if (!ultimoContato) return "nunca";
  const segundos = (agora.getTime() - ultimoContato.getTime()) / 1000;
  return segundos <= SEM_SINAL_SEGUNDOS ? "viva" : "sumida";
}

// —— O cruzamento, que e o motivo do CRM existir ——————————————————————————————

export interface LicencaParaCRM {
  venda: Venda;
  status: "active" | "past_due" | "canceled";
  ultimoContato: Date | null;
}

// 🔴 O valor do CRM nao esta em nenhuma das duas colunas sozinha: esta em quem aparece numa e NAO na
// outra. Por isso as duas sao mostradas lado a lado em vez de uma so "ativa".
export type Atencao =
  // Paga e nao usa. E cancelamento chegando: ninguem renova o que nao abre.
  | "paga-e-nao-usa"
  // Usa e nao paga. E cobranca a fazer: o TESTE que virou producao sem ninguem notar, ou a licenca
  // cancelada que continua rodando. A cortesia NAO entra — ver `atencaoDe`.
  | "usa-e-nao-paga"
  | null;

export function atencaoDe(l: LicencaParaCRM, agora: Date): Atencao {
  const sinal = sinalDe(l.ultimoContato, agora);
  const contratada = l.venda === "sold" && l.status !== "canceled";
  // 🔴 Cortesia NAO e lacuna de cobranca: e uma decisao ja tomada de nao cobrar. Alertar "cobranca
  // a fazer" sobre ela contradiz a propria palavra, e um alerta que nunca se resolve ensina quem
  // olha a ignorar a tela inteira — o oposto do que esta pagina existe para fazer.
  //
  // ⚠️ Cancelada encerra a cortesia. Continuar rodando depois disso e lacuna de novo, venha de onde
  // vier — por isso a condicao exige que ainda NAO esteja cancelada.
  const gratisDeProposito = l.venda === "courtesy" && l.status !== "canceled";

  // ⚠️ `nunca` fica de fora dos DOIS alertas de proposito. Sem contato nenhum nao da para dizer se
  // a pessoa usa: chamar isso de "paga e nao usa" acusaria quem comprou ontem.
  if (contratada && sinal === "sumida") return "paga-e-nao-usa";
  if (!contratada && !gratisDeProposito && sinal === "viva")
    return "usa-e-nao-paga";
  return null;
}

// —— O resumo do topo ————————————————————————————————————————————————————————

export interface Resumo {
  vendidas: number;
  vivas: number;
  nuncaFalaram: number;
  pagaENaoUsa: number;
  usaENaoPaga: number;
}

export function resumir(
  licencas: readonly LicencaParaCRM[],
  agora: Date,
): Resumo {
  const r: Resumo = {
    vendidas: 0,
    vivas: 0,
    nuncaFalaram: 0,
    pagaENaoUsa: 0,
    usaENaoPaga: 0,
  };
  for (const l of licencas) {
    // Cancelada nao conta como venda ativa: ela FOI vendida, e o total do topo responde "quantos
    // clientes eu tenho hoje", nao "quantos ja tive".
    if (l.venda === "sold" && l.status !== "canceled") r.vendidas++;
    const sinal = sinalDe(l.ultimoContato, agora);
    if (sinal === "viva") r.vivas++;
    if (sinal === "nunca") r.nuncaFalaram++;
    const a = atencaoDe(l, agora);
    if (a === "paga-e-nao-usa") r.pagaENaoUsa++;
    if (a === "usa-e-nao-paga") r.usaENaoPaga++;
  }
  return r;
}
